class Ingredients extends HTMLElement {
  #ingredients = [...this.querySelectorAll('[data-ingredient-wrapper]')];
  #ingredientsContent = [...this.querySelectorAll('[data-ingredient]')];
  #dots = [...this.querySelectorAll('[data-ingredient-dot]')];
  #maxHeight = 0;
  #mobileMq = window.matchMedia('(max-width: 1024px)');
  /** Real hover (mouse/trackpad): not primary touch UI — avoids first tap activating then click toggling off. */
  #hoverPointerMq = window.matchMedia('(hover: hover) and (pointer: fine)');

  /** Desktop layout: hover drives active; narrow / touch layouts use click on dot or image. */
  #useHoverActivation() {
    return !this.#mobileMq.matches && this.#hoverPointerMq.matches;
  }

  connectedCallback() {
    this.#ingredients.forEach((ingredient) => {
      const ingredientToggle = ingredient.querySelector('img');
      ingredientToggle.addEventListener('click', () => this.#handleClickToggle(ingredient));

      ingredientToggle.addEventListener('mouseenter', () => {
        if (this.#useHoverActivation() && !ingredient.classList.contains('active')) {
          this.#activateExclusive(ingredient.dataset.ingredientId);
        }
      });
    });

    this.#dots.forEach((dot) => {
      dot.addEventListener('click', () => this.#handleClickDot(dot));

      dot.addEventListener('mouseenter', () => {
        if (!this.#useHoverActivation()) return;
        const id = dot.dataset.ingredientId;
        const wrapper = this.#ingredients.find((ing) => ing.dataset.ingredientId === id);
        if (wrapper && !wrapper.classList.contains('active')) {
          this.#activateExclusive(id);
        }
      });
    });

    if (this.#mobileMq.matches) {
      this.#setMarginBottom();
      this.#positionConnectors();
    }

    this.#mobileMq.addEventListener('change', (event) => {
      if (event.matches) {
        this.#setMarginBottom();
        this.#positionConnectors();
        this.#replayConnectorAnimationsForActive();
      } else {
        this.style.marginBottom = '';
      }
    });

    window.addEventListener('resize', () => {
      if (this.#mobileMq.matches) {
        this.#setMarginBottom();
        this.#positionConnectors();
      } else {
        this.style.marginBottom = '';
      }
    });
  }

  /**
   * Connectors are `display: none` on desktop. Crossing to mobile with `.active`
   * already set skips the clip-path transition. Briefly clear active, then restore.
   */
  #replayConnectorAnimationsForActive() {
    const activeId = this.#ingredients.find((w) => w.classList.contains('active'))?.dataset
      .ingredientId;
    if (activeId === undefined || activeId === '') return;

    this.#ingredients.forEach((w) => w.classList.remove('active'));
    this.#dots.forEach((d) => d.classList.remove('active'));

    requestAnimationFrame(() => {
      this.#activateExclusive(activeId);
    });
  }

  #positionConnectors() {
    const containerRect = this.getBoundingClientRect();

    this.#ingredients.forEach((wrapper) => {
      const dot = wrapper.querySelector('[data-ingredient-dot]');
      const card = wrapper.querySelector('[data-ingredient]');
      const connector = wrapper.querySelector('.ingredient-connector');

      if (!dot || !card || !connector) return;

      const dotRect = dot.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      const left = dotRect.left + dotRect.width / 2 - containerRect.left;
      const top = dotRect.bottom - containerRect.top;
      const height = Math.max(0, cardRect.top - containerRect.top - top);

      connector.style.left = `${left}px`;
      connector.style.top = `${top}px`;
      connector.style.height = `${height}px`;
    });
  }

  #setMarginBottom() {
    this.#maxHeight = 0;
    this.#ingredientsContent.forEach((content) => {
      this.#maxHeight = Math.max(this.#maxHeight, content.offsetHeight);
    });
    this.style.marginBottom = `${this.#maxHeight}px`;
  }

  /** Set exactly one ingredient (wrapper + dot) as .active; used for desktop hover. */
  #activateExclusive(ingredientId) {
    if (ingredientId === null || ingredientId === undefined || ingredientId === '') return;

    for (const ing of this.#ingredients) {
      ing.classList.toggle('active', ing.dataset.ingredientId === ingredientId);
    }
    for (const dot of this.#dots) {
      dot.classList.toggle('active', dot.dataset.ingredientId === ingredientId);
    }
  }

  #clearAllActive() {
    this.#ingredients.forEach((ing) => ing.classList.remove('active'));
    this.#dots.forEach((dot) => dot.classList.remove('active'));
  }

  /**
   * Image click: mobile / tablet / keyboard. Exclusive selection; tap again to close.
   * `ingredient` is the [data-ingredient-wrapper] node (the handler passes the wrapper).
   */
  #handleClickToggle(ingredient) {
    const ingredientId = ingredient.dataset.ingredientId;
    if (ingredient.classList.contains('active')) {
      this.#clearAllActive();
    } else {
      this.#activateExclusive(ingredientId);
    }
  }

  /** Dot click (visible below lg). Same toggle semantics as image. */
  #handleClickDot(dot) {
    const ingredientId = dot.dataset.ingredientId;
    if (dot.classList.contains('active')) {
      this.#clearAllActive();
    } else {
      this.#activateExclusive(ingredientId);
    }
  }
}

customElements.define('ingredients-wrapper', Ingredients);
